/**
 * UI Utilities
 */

import { useState, useCallback } from 'react';

/**
 * Custom hook for copy-to-clipboard with feedback
 */
export function useCopyFeedback(duration: number = 2000) {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  const copy = useCallback(
    async (text: string, identifier: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedItem(identifier);
        setTimeout(() => setCopiedItem(null), duration);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    },
    [duration],
  );

  return { copy, isCopied: copiedItem };
}

/**
 * Mask sensitive data (like API keys)
 */
export function maskSensitiveData(value: string, visibleChars: number = 8): string {
  if (!value || value.length <= visibleChars) {
    return value;
  }
  return '•'.repeat(value.length - visibleChars) + value.slice(-visibleChars);
}

/**
 * Format date for display
 */
export function formatDate(dateString?: string): string {
  if (!dateString) return 'Unknown';

  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Debounce utility
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
